import { type GraphQLTaggedNode } from 'react-relay';
import { type IEnvironment } from 'relay-runtime';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { type ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { type ErrorPolicy, fetchQueryWithErrorPolicy } from './fetchQuery.ts';
import { isNoStoreFetchPolicy, mapFetchPolicyForQuery } from './mapFetchPolicyForQuery.ts';
import { type QueryInteropFetchPolicy } from './mapFetchPolicyForQuery.ts';

export type ExecuteRelayQueryArgs<TVariables extends Record<string, any>> = {
	/**
	 * The environment to execute against. Deliberately a parameter rather than resolved in here,
	 * because that is the one thing the two callers must NOT share: imperative helpers read
	 * `window.GLOBAL_RELAY_ENV`, while hooks must use `useRelayEnvironment()` so the read happens
	 * against the tree's own environment. Using the global from a hook produces server-empty /
	 * client-populated divergence and page-wide hydration mismatches — see the note on
	 * `useQueryNoFurtherUpdateInteropRelay`.
	 */
	environment: IEnvironment;
	query: GraphQLTaggedNode;
	variables: TVariables;
	fetchPolicy?: QueryInteropFetchPolicy;
	errorPolicy?: ErrorPolicy;
	context?: Record<string, any>;
};

export type ExecuteRelayQueryResult<TData> = {
	data: TData;
	error?: ApolloErrorInterop;
};

/**
 * The single Relay read used by every promise-shaped interop path.
 *
 * Exists so the fetch-policy mapping and the `noStore` derivation have exactly one home. Both were
 * previously inlined per call site, and `noStore` in particular is easy to omit silently: a path that
 * forgets it still compiles and still fetches, but normalizes the response — which is precisely the
 * record collision `'no-store'` exists to prevent, and on the SSR path it poisons the store that gets
 * serialized into `__RELAY_STORE__` and hydrated on the client.
 *
 * Callers own their own promise semantics. `queryInterop` hands `{ data, error }` back to the caller;
 * `useLazyQueryPromiseInterop` rejects, because the Apollo hook it mirrors rejects.
 */
export async function executeRelayQuery<TData, TVariables extends Record<string, any>>({
	environment,
	query,
	variables,
	fetchPolicy,
	errorPolicy,
	context,
}: ExecuteRelayQueryArgs<TVariables>): Promise<ExecuteRelayQueryResult<TData>> {
	const result = await fetchQueryWithErrorPolicy<{ response: TData; variables: TVariables }>(
		environment,
		query,
		variables,
		{
			fetchPolicy: mapFetchPolicyForQuery(fetchPolicy),
			networkCacheConfig: { metadata: context },
		},
		{ errorPolicy, noStore: isNoStoreFetchPolicy(fetchPolicy) },
	).toPromise();

	return {
		data: result?.data as TData,
		error: result?.error,
	};
}
